import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Tv, Video, Music, Home, Menu, X, ChevronLeft, ChevronRight, Radio } from "lucide-react";

const navItems = [
  { label: "Inicio", icon: Home, page: "Home" },
  { label: "TV en Vivo", icon: Tv, page: "TvEnVivo" },
  { label: "Videos", icon: Video, page: "Videos" },
  { label: "Audios MP3", icon: Music, page: "AudiosMP3" },
  { label: "Administrar", icon: Radio, page: "Admin" },
];

export default function Layout({ children, currentPageName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const location = useLocation();
  const intervalRef = useRef(null);

  // Auto-rotate menu highlight
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setScrollIndex((prev) => (prev + 1) % navItems.length);
    }, 3000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handlePrev = () => {
    clearInterval(intervalRef.current);
    setScrollIndex((prev) => (prev - 1 + navItems.length) % navItems.length);
  };

  const handleNext = () => {
    clearInterval(intervalRef.current);
    setScrollIndex((prev) => (prev + 1) % navItems.length);
  };

  const isActive = (page) => currentPageName === page;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <style>{`
        :root {
          --background: hsl(0 0% 0%);
          --foreground: hsl(0 0% 100%);
          --card: hsl(0 0% 8%);
          --card-foreground: hsl(0 0% 100%);
          --primary: hsl(45 100% 50%);
          --primary-foreground: hsl(0 0% 0%);
          --muted: hsl(0 0% 15%);
          --muted-foreground: hsl(0 0% 70%);
          --border: hsl(0 0% 20%);
          --accent: hsl(45 100% 50%);
        }
        * { box-sizing: border-box; }
        body { background: #000; color: #fff; margin: 0; }
        .nav-glow {
          box-shadow: 0 0 20px rgba(255, 200, 0, 0.4), 0 0 40px rgba(255, 200, 0, 0.2);
        }
        .logo-glow {
          text-shadow: 0 0 20px rgba(255, 200, 0, 0.8), 0 0 40px rgba(255, 200, 0, 0.4);
        }
        .menu-item-active {
          background: linear-gradient(135deg, #ffc800, #ff8c00);
          color: #000;
          box-shadow: 0 0 15px rgba(255, 200, 0, 0.6);
        }
        .menu-item-highlight {
          border: 1px solid rgba(255, 200, 0, 0.5);
          background: rgba(255, 200, 0, 0.1);
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes pulse-gold {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .live-dot { animation: pulse-gold 1.5s infinite; }
        @keyframes slide-in {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .slide-in { animation: slide-in 0.3s ease-out; }
        .tv-scanline {
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.03) 2px,
            rgba(0,0,0,0.03) 4px
          );
          pointer-events: none;
          position: fixed;
          inset: 0;
          z-index: 9999;
        }
      `}</style>

      {/* TV Scanline overlay */}
      <div className="tv-scanline" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-yellow-500/30 backdrop-blur-sm">
        <div className="flex items-center justify-between px-3 py-2 md:px-6">
          {/* Logo */}
          <Link to={createPageUrl("Home")} className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Tv className="w-4 h-4 md:w-5 md:h-5 text-black" />
            </div>
            <div>
              <span className="logo-glow text-lg md:text-2xl font-black tracking-wider text-yellow-400">
                LUISMUSIC
              </span>
              <span className="text-lg md:text-2xl font-black tracking-wider text-white">TVS</span>
              <div className="flex items-center gap-1 -mt-1">
                <span className="live-dot w-2 h-2 rounded-full bg-red-500 inline-block" />
                <span className="text-xs text-red-400 font-bold">EN VIVO</span>
              </div>
            </div>
          </Link>

          {/* Desktop Nav - Rotativo */}
          <nav className="hidden md:flex items-center gap-1 relative">
            <button
              onClick={handlePrev}
              className="p-1 text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1">
              {navItems.map((item, idx) => {
                const Icon = item.icon;
                const active = isActive(item.page);
                const highlighted = scrollIndex === idx;
                return (
                  <Link
                    key={item.page}
                    to={createPageUrl(item.page)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                      active
                        ? "menu-item-active"
                        : highlighted
                        ? "menu-item-highlight text-yellow-300"
                        : "text-gray-300 hover:text-yellow-400 hover:bg-white/5"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <button
              onClick={handleNext}
              className="p-1 text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Rotativo Nav */}
        <div className="md:hidden overflow-x-auto scrollbar-hide border-t border-yellow-500/20">
          <div className="flex items-center gap-2 px-3 py-2 min-w-max">
            {navItems.map((item, idx) => {
              const Icon = item.icon;
              const active = isActive(item.page);
              const highlighted = scrollIndex === idx;
              return (
                <Link
                  key={item.page}
                  to={createPageUrl(item.page)}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-xs whitespace-nowrap transition-all duration-300 ${
                    active
                      ? "menu-item-active"
                      : highlighted
                      ? "menu-item-highlight text-yellow-300"
                      : "text-gray-400 border border-gray-700"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/80"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-72 bg-gray-950 border-r border-yellow-500/30 slide-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                  <Tv className="w-6 h-6 text-black" />
                </div>
                <div>
                  <div className="text-xl font-black text-yellow-400">LUISMUSIC</div>
                  <div className="text-xl font-black text-white -mt-1">TVS</div>
                </div>
              </div>
              <nav className="flex flex-col gap-2">
                {navItems.map((item, idx) => {
                  const Icon = item.icon;
                  const active = isActive(item.page);
                  return (
                    <Link
                      key={item.page}
                      to={createPageUrl(item.page)}
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-base transition-all duration-200 ${
                        active
                          ? "menu-item-active"
                          : "text-gray-300 hover:text-yellow-400 hover:bg-white/5"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 relative z-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-yellow-500/20 bg-black py-4 px-4 text-center">
        <p className="text-gray-500 text-xs">
          © {new Date().getFullYear()} <span className="text-yellow-400 font-bold">LUISMUSICTVS</span> — Todos los derechos reservados
        </p>
        <p className="text-gray-600 text-xs mt-1">TV en Vivo · Videos · Música MP3</p>
      </footer>
    </div>
  );
}